import { faPencil, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FlaskConical, Users, File, Search, ExternalLink } from 'lucide-react';

import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useCreateCase } from '@/api/mutations/useCreateCase';
import Button from '@/components/atoms/Button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/Popover';
import formatDate from '@/helpers/formatDate';
import { cn } from '@/helpers/shadCnUtils';
import { Matter, SearchedMatter } from '@/types';

export interface MatterItemProps {
  matter: Matter;
  isCreator: boolean;
  isSuperAdmin: boolean;
  onEdit: (id: string) => void;
  onView: (id: string) => void;
  onViewMatchedCase?: (id: string) => void;
  personalMatter?: boolean;
  testid?: string;
  matchedCases?: SearchedMatter['matchedCases'];
}

export const MatterItem = ({
  matter,
  isCreator,
  onEdit,
  onView,
  onViewMatchedCase,
  isSuperAdmin,
  testid,
  personalMatter = false,
  matchedCases,
}: MatterItemProps) => {
  const { mutate: createCase } = useCreateCase(matter.type);

  return (
    <div
      data-testid={testid}
      className={cn(
        'group relative overflow-hidden rounded-lg border bg-gradient-to-br from-white to-gray-50 px-4 py-3 shadow transition-all duration-200',
        personalMatter
          ? 'border-2 border-purple-400 bg-gradient-to-br from-purple-50/50 to-white'
          : 'border-gray-200 hover:border-blue-200 hover:shadow-lg',
      )}
    >
      {/* Header Section */}
      <div className="flex items-center justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-4">
            <h3
              className={cn(
                'max-w-2/3 truncate text-sm font-semibold text-gray-900',
                personalMatter && 'text-purple-700',
              )}
              data-testid="matter-name"
            >
              {matter.name}
            </h3>
            {matter.code && (
              <span
                className="flex-shrink-0 rounded bg-gray-100 px-1 py-0 font-mono text-[10px] font-medium text-gray-600"
                data-testid="matter-code"
              >
                {matter.code}
              </span>
            )}
          </div>
        </div>

        {matchedCases && matchedCases.length > 0 && (
          <div className="ml-1 flex-shrink-0">
            <Popover>
              <PopoverTrigger asChild>
                <button className="group/trigger">
                  <div className="flex items-center gap-1 rounded-full border border-dashed border-blue-300 bg-blue-50 px-1.5 py-0 transition-all duration-200 hover:border-blue-400 hover:bg-blue-100 hover:shadow-sm">
                    <Search className="h-2.5 w-2.5 text-blue-600 transition-colors duration-200" />
                    <span className="text-[11px] font-medium text-blue-700">
                      {matchedCases.length} search {matchedCases.length === 1 ? 'match' : 'matches'}
                    </span>
                  </div>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-0 shadow-xl" side="bottom" align="end">
                <div className="rounded-lg border-0 bg-white">
                  {/* Header */}
                  <div className="rounded-t bg-blue-50 px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100">
                        <Search className="h-3 w-3 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="text-xs font-semibold text-gray-900">Matched Cases</h4>
                        <p className="text-[10px] text-gray-600">
                          {matchedCases.length} case{matchedCases.length === 1 ? '' : 's'} found
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Cases List */}
                  <div className="max-h-48 overflow-y-auto p-1.5">
                    <div className="flex flex-col gap-0.5">
                      {matchedCases.map((matchedCase, index) => (
                        <button
                          key={matchedCase.id}
                          className="group/case flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-all duration-200 hover:bg-blue-50 hover:shadow-sm"
                          onClick={() => {
                            trackCustomEvent(TrackingEvents.ViewCaseFromMatterSearchMatches, {
                              caseId: matchedCase.id,
                            });
                            onViewMatchedCase && onViewMatchedCase(matchedCase.id);
                          }}
                        >
                          <div className="flex h-5 w-5 items-center justify-center rounded-full bg-gray-100 transition-colors duration-200 group-hover/case:bg-blue-100">
                            <File className="h-2.5 w-2.5 text-gray-600 group-hover/case:text-blue-600" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-xs font-medium text-gray-900 group-hover/case:text-blue-900">
                              {matchedCase.name}
                            </p>
                          </div>
                          <ExternalLink className="h-3 w-3 text-gray-400 opacity-0 transition-all duration-200 group-hover/case:text-blue-500 group-hover/case:opacity-100" />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        )}
        {/* Status Badge */}
        {personalMatter ? (
          <div className="ml-1 flex-shrink-0">
            <div className="flex items-center gap-1 rounded-full bg-purple-100 px-1.5 py-0">
              <FlaskConical className="h-2.5 w-2.5 text-purple-600" />
              <span className="text-[11px] font-medium text-purple-700" data-testid="personal-matter-tag">
                Personal Matter
              </span>
            </div>
          </div>
        ) : (
          <div className="ml-1 flex-shrink-0">
            <div className="flex items-center gap-1 rounded-full bg-blue-50 px-1.5 py-0">
              <Users className="h-2.5 w-2.5 text-blue-600" />
              <span className="text-[11px] font-medium text-blue-700">Shared Matter</span>
            </div>
          </div>
        )}
      </div>

      {/* Description */}
      {matter.description && (
        <p className="line-clamp-1 truncate text-[11px] leading-tight text-gray-600" data-testid="matter-description">
          {matter.description}
        </p>
      )}

      {/* Footer Section */}
      <div className="mt-1 flex items-center justify-between border-t border-gray-100 pt-1.5">
        {/* Actions */}
        <div className="flex items-center gap-2">
          {personalMatter ? (
            <>
              <Button
                rounded="base"
                size="xs"
                text="View"
                type="primary"
                onClick={() => {
                  trackCustomEvent(TrackingEvents.ViewPersonalMatter, { matterId: matter.id });
                  onView(matter.id);
                }}
              />
              <Button
                rounded="base"
                size="xs"
                text="Upload"
                type="secondary"
                onClick={() => {
                  trackCustomEvent(TrackingEvents.PersonalMatterUploadShortcut, { matterId: matter.id });
                  createCase(matter.id);
                }}
                icon={<FontAwesomeIcon icon={faPlus} className="mr-1 h-2 w-2" />}
              />
            </>
          ) : (
            <>
              <Button
                rounded="base"
                size="xs"
                text="View"
                type="primary"
                data-testid="matter-view-btn"
                onClick={() => {
                  trackCustomEvent(TrackingEvents.ViewMatter, { matterId: matter.id });
                  onView(matter.id);
                }}
              />
              {(isCreator || isSuperAdmin) && (
                <Button
                  rounded="base"
                  size="xs"
                  text="Edit"
                  type="secondary"
                  onClick={() => {
                    trackCustomEvent(TrackingEvents.EditMatter, { matterId: matter.id });
                    onEdit(matter.id);
                  }}
                  data-testid="matter-edit-btn"
                  icon={<FontAwesomeIcon icon={faPencil} className="mr-1 h-2 w-2 text-gray-700" />}
                />
              )}
            </>
          )}
        </div>

        {/* Metadata */}
        <div className="flex items-center gap-1 text-[11px] text-gray-500">
          {matter.createdBy?.email && (
            <div className="flex items-center gap-0.5 rounded bg-gray-100 px-1 py-0">
              <span className="text-gray-600">Owner:</span>
              <span className="font-medium text-gray-700">{matter.createdBy.email}</span>
            </div>
          )}
          <div className="flex items-center gap-0.5 rounded bg-gray-100 px-1 py-0">
            <span className="text-gray-600">Created:</span>
            <span className="font-medium text-gray-700" data-testid="matter-created-date">
              {formatDate(matter.createdDate)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
